import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { ROOT_NODE_PATH } from "./builder";

export class ModelPath {
	private static readonly _root: ModelPath = new ModelPath(null, { kind: "root" });

	private readonly _parent: ModelPath | null;
	private readonly _data: ModelPathNodeData;

	private constructor(parent: ModelPath | null, data: ModelPathNodeData) {
		this._parent = parent;
		this._data = data;
	}

	public for(propertyName: string): ModelPath {
		return new ModelPath(
			this,
			{
				kind: "property",
				property: propertyName
			}
		);
	}

	public forIndex(index: number): ModelPath {
		return new ModelPath(
			this,
			{
				kind: "index",
				index
			}
		);
	}

	public equals(other: ModelPath): boolean {
		return ModelPath.areEqual(this, other);
	}

	public isAncestorOf(other: ModelPath): boolean {
		return isNotNil(other._parent)
			&& (
				this.equals(other._parent)
					|| this.isAncestorOf(other._parent)
			);
	}

	public isDescendentOf(other: ModelPath): boolean {
		return other.isAncestorOf(this);
	}

	public static get root(): ModelPath {
		return ModelPath._root;
	}

	public toString(): string {
		let result: string = this.formatData();

		if (this._parent) {
			result = this._parent.toString() + result;
		}

		return result;
	}

	private formatData(): string {
		switch (this._data.kind) {
			case "root":
				return ROOT_NODE_PATH;
			case "property":
				return `.${this._data.property}`;
			case "index":
				return `[${this._data.index}]`;
			default:
				never(this._data);
		}
	}

	private static areEqual(a: ModelPath | null, b: ModelPath | null): boolean {
		if (Object.is(a, b)) {
			return true;
		}

		if (!a || !b) {
			return false;
		}

		return ModelPath.isDataEqual(a._data, b._data)
			&& ModelPath.areEqual(a._parent, b._parent);
	}

	private static isDataEqual(a: ModelPathNodeData, b: ModelPathNodeData): boolean {
		switch (a.kind) {
			case "root":
				return b.kind === "root";
			case "property":
				return b.kind === "property"
					&& a.property === b.property;
			case "index":
				return b.kind === "index"
					&& a.index === b.index;
			default:
				never(a);
		}
	}
}

type RootModelPathNodeData = {
	kind: "root";
};

type PropertyModelPathNodeData = {
	kind: "property";
	property: string;
};

type IndexModelPathNodeData = {
	kind: "index";
	index: number;
};

type ModelPathNodeData =
	| RootModelPathNodeData
	| PropertyModelPathNodeData
	| IndexModelPathNodeData;